import * as THREE from 'three';
import { GameScene } from './Scene';
import { Player } from './Player';
import { Obstacle, ObstacleType } from './Obstacle';
import type { Character } from '@/schemas';
import { DEFAULT_GAME_CONFIG } from '@/schemas';
import { SoundManager } from '@/utils/SoundManager';
import type { AnimatedModelData } from '@/utils/AnimatedModelLoader';

export type GameState = 'ready' | 'playing' | 'paused' | 'gameOver';

export class Game {
  private scene: GameScene;
  private player: Player | null = null;
  private obstacles: Obstacle[] = [];
  private state: GameState = 'ready';
  private score: number = 0;
  private speed: number;
  private lastObstacleTime: number = 0;
  private obstacleSpawnInterval: number;
  private clock: THREE.Clock;
  private onScoreUpdate?: (score: number) => void;
  private onGameOver?: (score: number) => void;
  private onDifficultyIncrease?: () => void;
  private soundManager: SoundManager;
  private difficultyIncreaseShown: boolean = false; // 難易度上昇メッセージを表示したかどうか

  constructor(scene: GameScene) {
    this.scene = scene;
    this.speed = DEFAULT_GAME_CONFIG.initialSpeed;
    this.obstacleSpawnInterval = this.getRandomSpawnInterval();
    this.clock = new THREE.Clock();
    this.soundManager = new SoundManager();
  }

  /**
   * ゲームを開始
   */
  public start(character: Character, modelData: AnimatedModelData): void {
    // プレイヤーの作成
    this.player = new Player(character, modelData, this.soundManager);
    this.scene.scene.add(this.player.model);

    // デバッグ用：バウンディングボックスを追加（DEBUG_MODE時のみ）
    const boxHelper = this.player.getBoxHelper();
    if (boxHelper) {
      this.scene.scene.add(boxHelper);
    }

    // ゲーム状態をリセット
    this.state = 'playing';
    this.score = 0;
    this.speed = DEFAULT_GAME_CONFIG.initialSpeed;
    this.obstacles = [];
    this.lastObstacleTime = 0;
    this.difficultyIncreaseShown = false;
    this.clock.start();

    // 走るアニメーションを開始
    this.player.startRunning();
  }

  /**
   * ゲームを更新
   */
  public update(): void {
    if (this.state !== 'playing' || !this.player) return;

    const deltaTime = this.clock.getDelta();
    const elapsedTime = this.clock.getElapsedTime();

    // プレイヤーの更新
    this.player.update(deltaTime);

    // スコアの更新（経過時間ベース）
    const newScore = Math.floor(elapsedTime * 10);
    // スコアが変わった時だけコールバックを呼び出す（UI更新の最適化）
    if (newScore !== this.score) {
      this.score = newScore;
      if (this.onScoreUpdate) {
        this.onScoreUpdate(this.score);
      }

      // 1075点に到達したら難易度上昇メッセージを表示（実際の難易度上昇は1100点）
      if (this.score >= 1075 && !this.difficultyIncreaseShown) {
        this.difficultyIncreaseShown = true;
        if (this.onDifficultyIncrease) {
          this.onDifficultyIncrease();
        }
      }
    }

    // 速度の増加（線形、1100点以降は減速）
    if (elapsedTime >= 110) {
      // 1100点以降: 0.13で増加
      this.speed = 29.6 + ((elapsedTime - 110) * 0.13);
    } else {
      // 1100点まで: 0.16で増加
      this.speed = DEFAULT_GAME_CONFIG.initialSpeed +
                   (elapsedTime * DEFAULT_GAME_CONFIG.speedIncrement);
    }

    // 背景の更新（後ろに流れる）
    this.scene.updateBackground(this.speed, deltaTime);

    // 障害物の生成
    if (elapsedTime - this.lastObstacleTime > this.obstacleSpawnInterval) {
      this.spawnObstacle();
      this.lastObstacleTime = elapsedTime;
      this.obstacleSpawnInterval = this.getRandomSpawnInterval();
    }

    // 障害物の更新と削除
    for (let i = this.obstacles.length - 1; i >= 0; i--) {
      const obstacle = this.obstacles[i];
      obstacle.setSpeed(this.speed);
      obstacle.update(deltaTime);

      // 衝突判定
      if (this.checkCollision(this.player, obstacle)) {
        this.soundManager.playCollision();
        this.gameOver();
        return;
      }

      // 画面外に出た障害物を削除
      if (obstacle.isOutOfScreen()) {
        this.scene.scene.remove(obstacle.mesh);
        obstacle.dispose();
        this.obstacles.splice(i, 1);
      }
    }
  }

  /**
   * 障害物を生成
   */
  private spawnObstacle(): void {
    // 一時的に最初から障害物が出現するように設定（後で消す）
    // const canSpawnFlying = this.score >= 300;
    const canSpawnFlying = true; // 常に有効

    let obstacleType: ObstacleType;
    if (canSpawnFlying && Math.random() < 0.4) {
      // 40%の確率で空を飛ぶ障害物を生成
      const flyingType = Math.random() < 0.5 ? 'flying1' : 'flying2';
      obstacleType = flyingType;
    } else {
      // 地面の障害物
      obstacleType = 'ground';
    }

    // 1100点以上で30%の確率で透明化ブロックを生成
    const shouldFadeOut = this.score >= 1100 && Math.random() < 0.3;

    const obstacle = new Obstacle(obstacleType, this.speed, shouldFadeOut);
    this.scene.scene.add(obstacle.mesh);
    this.obstacles.push(obstacle);
  }

  /**
   * ランダムな障害物出現間隔を取得
   */
  private getRandomSpawnInterval(): number {
    const min = DEFAULT_GAME_CONFIG.obstacleSpawnInterval.min / 1000;
    const max = DEFAULT_GAME_CONFIG.obstacleSpawnInterval.max / 1000;
    return Math.random() * (max - min) + min;
  }

  /**
   * 衝突判定
   */
  private checkCollision(player: Player, obstacle: Obstacle): boolean {
    // まず、X軸方向の重なりを簡単にチェック（高速化）
    // getPosition()はclone()を返すので、直接mesh.positionを使用して最適化
    const playerModel = player.model;
    const obstacleMesh = obstacle.mesh;
    const playerBox = player.getBoundingBox();
    const playerWidth = playerBox.max.x - playerBox.min.x;
    const playerCenterX = playerModel.position.x;
    const obstacleX = obstacleMesh.position.x;
    
    // X軸方向で重なっていない場合は早期リターン
    if (playerCenterX + playerWidth / 2 < obstacleX - 0.5 || 
        playerCenterX - playerWidth / 2 > obstacleX + 0.5) {
      return false;
    }

    // 2段目の障害物（flyingHeight === 2.5）は、プレイヤーがジャンプしている時のみ衝突判定
    if (obstacle.isFlying && obstacle.flyingHeight === 2.5) {
      // プレイヤーが地面にいる場合は衝突しない（ジャンプしないと避けられない）
      if (player.getIsGrounded()) {
        return false;
      }

      // さらに、プレイヤーのY位置が2段目の障害物の高さ付近にある時のみ衝突判定
      // プレイヤーの中心位置を取得（直接model.positionを使用）
      const playerY = playerModel.position.y;
      const playerHeight = playerBox.max.y - playerBox.min.y;
      const playerTop = playerY + playerHeight / 2;
      const playerBottom = playerY - playerHeight / 2;

      // 2段目の障害物の高さ範囲（約2.0〜3.0）
      const obstacleTop = obstacle.flyingHeight + 0.5;
      const obstacleBottom = obstacle.flyingHeight - 0.5;

      // プレイヤーが2段目の障害物の高さ範囲にいない場合は衝突しない
      if (playerTop < obstacleBottom || playerBottom > obstacleTop) {
        return false;
      }
    }

    // 最終的な詳細な衝突判定
    const obstacleBox = obstacle.getBoundingBox();
    return playerBox.intersectsBox(obstacleBox);
  }

  /**
   * ゲームオーバー処理
   */
  private gameOver(): void {
    this.state = 'gameOver';
    this.clock.stop();

    if (this.onGameOver) {
      this.onGameOver(this.score);
    }
  }

  /**
   * プレイヤーをジャンプさせる
   */
  public jump(): void {
    if (this.state === 'playing' && this.player) {
      // AudioContextを有効化（ユーザーインタラクション後）
      this.soundManager.enableAudioContext();
      this.player.jump();
    }
  }

  /**
   * ゲームを一時停止
   */
  public pause(): void {
    if (this.state === 'playing') {
      this.state = 'paused';
      this.clock.stop();
    }
  }

  /**
   * ゲームを再開
   */
  public resume(): void {
    if (this.state === 'paused') {
      this.state = 'playing';
      this.clock.start();
    }
  }

  /**
   * ゲームをリセット
   */
  public reset(): void {
    // プレイヤーの削除
    if (this.player) {
      this.scene.scene.remove(this.player.model);

      // バウンディングボックスヘルパーも削除（ある場合）
      const boxHelper = this.player.getBoxHelper();
      if (boxHelper) {
        this.scene.scene.remove(boxHelper);
      }

      this.player.dispose();
      this.player = null;
    }

    // 全ての障害物を削除
    for (const obstacle of this.obstacles) {
      this.scene.scene.remove(obstacle.mesh);
      obstacle.dispose();
    }
    this.obstacles = [];

    // 状態をリセット
    this.state = 'ready';
    this.score = 0;
    this.speed = DEFAULT_GAME_CONFIG.initialSpeed;
    this.lastObstacleTime = 0;
    this.difficultyIncreaseShown = false;
    this.clock.stop();
  }

  /**
   * 現在のスコアを取得
   */
  public getScore(): number {
    return this.score;
  }

  /**
   * ゲームの状態を取得
   */
  public getState(): GameState {
    return this.state;
  }

  /**
   * スコア更新時のコールバックを設定
   */
  public setOnScoreUpdate(callback: (score: number) => void): void {
    this.onScoreUpdate = callback;
  }

  /**
   * ゲームオーバー時のコールバックを設定
   */
  public setOnGameOver(callback: (score: number) => void): void {
    this.onGameOver = callback;
  }

  /**
   * 難易度上昇時のコールバックを設定
   */
  public setOnDifficultyIncrease(callback: () => void): void {
    this.onDifficultyIncrease = callback;
  }
}
